// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Clause List - displays identified clauses (FAR, regulatory, etc.) as clean cards.
 */
import React from 'react'
import { cn, Badge, Card, MarkdownViewer } from '@/components/ui'
import { AlertTriangle, FileText } from 'lucide-react'
import { formatCount } from '@/applications/contract-review/utils'
import { useServices } from '@/app-shell/services'
import { getPageViewUrl } from '@/subsystems/documents'

/** Generic clause item - maps to API's clauses array */
export interface IdentifiedClause {
  id: string
  text: string
  reference?: string | null
  clauseText?: string | null
  pageNumber?: number | null
}

interface Props {
  title?: string
  items: IdentifiedClause[]
  countLabel?: string
  className?: string
  documentId?: string
  orgId?: string
  workspaceId?: string
  version?: number
}

export const ClauseList: React.FC<Props> = ({
  title = 'Identified Clauses',
  items,
  countLabel = 'clause',
  className,
  documentId,
  orgId,
  workspaceId,
  version,
}) => {
  const services = useServices()

  const handleOpenPagePdf = async (pageNumber: number) => {
    if (!documentId || !orgId || !workspaceId) return
    try {
      const apiClient = services.getClient()
      const url = await getPageViewUrl(apiClient, orgId, workspaceId, documentId, pageNumber, { version })
      window.open(url, '_blank')
    } catch (err) {
      console.error('[ClauseList] Failed to get page view URL:', err)
    }
  }

  const canLinkToPages = Boolean(documentId && orgId && workspaceId)

  const withPageNumber = items.filter(c => c.pageNumber != null).length
  const hasNoPageNumbers = items.length > 0 && withPageNumber === 0
  const hasPartialPageNumbers = withPageNumber > 0 && withPageNumber < items.length

  if (items.length === 0) {
    return (
      <div className={cn('flex min-h-48 items-center justify-center rounded-lg border border-dashed bg-muted/20 p-8 text-center text-muted-foreground', className)}>
        No clauses found in this document.
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <Badge
          variant="outline"
          className="border px-3 py-1 text-xs font-semibold uppercase tracking-wide"
        >
          {formatCount(items.length, countLabel)}
        </Badge>
      </div>

      {hasNoPageNumbers && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Page numbers unavailable — clauses may not be in document order.</span>
        </div>
      )}
      {hasPartialPageNumbers && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Some clauses missing page numbers — order may not match document.</span>
        </div>
      )}

      <div className="space-y-3">
        {items.map(item => (
          <Card key={item.id} className="relative border bg-card">
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-slate-300 dark:bg-slate-600" />

            <div className="py-4 pl-5 pr-4">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 text-xs text-foreground/70">
                    {item.reference && (
                      <span className="font-medium">{item.reference}</span>
                    )}
                    {item.reference && item.pageNumber && (
                      <span className="opacity-50">•</span>
                    )}
                    {item.pageNumber && (
                      canLinkToPages ? (
                        <button
                          onClick={() => void handleOpenPagePdf(item.pageNumber!)}
                          className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          <FileText className="h-3 w-3" />
                          <span className="hover:underline underline-offset-2">Page {item.pageNumber}</span>
                        </button>
                      ) : (
                        <span>Page {item.pageNumber}</span>
                      )
                    )}
                  </div>

                  <h3 className="font-mono text-base font-semibold text-foreground">
                    {item.text}
                  </h3>
                </div>
              </div>

              {item.clauseText && (
                <div className="text-sm text-foreground/80 leading-relaxed [&_strong]:text-foreground [&_strong]:font-semibold">
                  <MarkdownViewer content={item.clauseText} />
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}
